import { useState, useEffect } from 'react';
import { X, Plus, ChevronRight, FileText, Euro, Calendar, Edit, Receipt } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { CreateFundCallModal } from './CreateFundCallModal';
import { FundCallDetailModal } from './FundCallDetailModal';
import { EditSubsidyModal } from './EditSubsidyModal';
import { TaxReceiptsTab } from './TaxReceiptsTab';

interface Subsidy {
  id: string;
  name: string;
  funder: string;
  allocated_amount: number;
  start_date: string;
  end_date: string;
  status: 'en_cours' | 'cloturee';
  notes: string;
}

interface FundCall {
  id: string;
  name: string;
  call_date: string;
  requested_amount: number;
  received_amount: number;
  usage_description: string;
}

interface SubsidyDetailModalProps {
  subsidy: Subsidy;
  onClose: () => void;
  onUpdate: () => void;
}

export function SubsidyDetailModal({ subsidy, onClose, onUpdate }: SubsidyDetailModalProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'tax-receipts'>('overview');
  const [fundCalls, setFundCalls] = useState<FundCall[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateFundCall, setShowCreateFundCall] = useState(false);
  const [selectedFundCall, setSelectedFundCall] = useState<FundCall | null>(null);
  const [showEditSubsidy, setShowEditSubsidy] = useState(false);
  const [userId, setUserId] = useState<string>('');

  useEffect(() => {
    loadUser();
    loadFundCalls();
  }, [subsidy.id]);

  const loadUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) setUserId(user.id);
  };

  const loadFundCalls = async () => {
    try {
      const { data, error } = await supabase
        .from('grant_fund_calls')
        .select('*')
        .eq('subsidy_id', subsidy.id)
        .order('call_date', { ascending: false });

      if (error) throw error;
      setFundCalls(data || []);
    } catch (error) {
      console.error('Error loading fund calls:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const formatDate = (date: string) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('fr-FR');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{subsidy.name}</h2>
            <p className="text-sm text-gray-600 mt-1">{subsidy.funder}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 px-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'overview'
                  ? 'border-emerald-500 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FileText className="w-5 h-5" />
              Tableau de bord
            </button>
            <button
              onClick={() => setActiveTab('tax-receipts')}
              className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'tax-receipts'
                  ? 'border-emerald-500 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Receipt className="w-5 h-5" />
              Reçus fiscaux
            </button>
          </nav>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {activeTab === 'overview' && (
            <>
          {/* Subsidy info */}
          <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-gray-600">Montant attribué</div>
              <div className="text-lg font-semibold text-gray-900 flex items-center gap-2 mt-1">
                <Euro className="w-5 h-5" />
                {formatCurrency(subsidy.allocated_amount)}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Période</div>
              <div className="text-lg font-semibold text-gray-900 flex items-center gap-2 mt-1">
                <Calendar className="w-5 h-5" />
                {subsidy.start_date && subsidy.end_date
                  ? `${formatDate(subsidy.start_date)} - ${formatDate(subsidy.end_date)}`
                  : '-'}
              </div>
            </div>
            {subsidy.notes && (
              <div className="col-span-2">
                <div className="text-sm text-gray-600 mb-1">Notes</div>
                <div className="text-sm text-gray-900">{subsidy.notes}</div>
              </div>
            )}
          </div>

          {/* Fund calls section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Appels de fonds (ADF)</h3>
              <button
                onClick={() => setShowCreateFundCall(true)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Nouvel ADF
              </button>
            </div>

            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600 mx-auto"></div>
              </div>
            ) : fundCalls.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <FileText className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                <p className="text-sm text-gray-600 mb-3">Aucun appel de fonds pour cette subvention</p>
                <button
                  onClick={() => setShowCreateFundCall(true)}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Créer le premier ADF
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {fundCalls.map((fundCall) => (
                  <div
                    key={fundCall.id}
                    className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg hover:border-emerald-300 hover:shadow-sm transition-all cursor-pointer"
                    onClick={() => setSelectedFundCall(fundCall)}
                  >
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{fundCall.name}</div>
                      {fundCall.usage_description && (
                        <div className="text-sm text-gray-600 mt-1">{fundCall.usage_description}</div>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-sm">
                        <div className="text-gray-600">
                          Date: <span className="font-medium">{formatDate(fundCall.call_date)}</span>
                        </div>
                        <div className="text-gray-600">
                          Demandé: <span className="font-medium text-emerald-600">{formatCurrency(fundCall.requested_amount)}</span>
                        </div>
                        {fundCall.received_amount && (
                          <div className="text-gray-600">
                            Versé: <span className="font-medium text-green-600">{formatCurrency(fundCall.received_amount)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                ))}
              </div>
            )}
          </div>
            </>
          )}

          {activeTab === 'tax-receipts' && userId && (
            <TaxReceiptsTab
              associationId={subsidy.id}
              associationName={subsidy.name}
              userId={userId}
            />
          )}
        </div>

        {activeTab === 'overview' && (
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={() => setShowEditSubsidy(true)}
            className="flex items-center gap-2 px-4 py-2 text-emerald-600 border border-emerald-600 rounded-lg hover:bg-emerald-50 transition-colors"
          >
            <Edit className="w-4 h-4" />
            Modifier la subvention
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Fermer
          </button>
        </div>
        )}
      </div>

      {/* Modals */}
      {showEditSubsidy && (
        <EditSubsidyModal
          subsidy={subsidy}
          onClose={() => setShowEditSubsidy(false)}
          onSuccess={() => {
            setShowEditSubsidy(false);
            onUpdate();
          }}
        />
      )}

      {showCreateFundCall && (
        <CreateFundCallModal
          subsidyId={subsidy.id}
          subsidyName={subsidy.name}
          onClose={() => setShowCreateFundCall(false)}
          onSuccess={() => {
            setShowCreateFundCall(false);
            loadFundCalls();
          }}
        />
      )}

      {selectedFundCall && (
        <FundCallDetailModal
          fundCall={selectedFundCall}
          subsidyName={subsidy.name}
          onClose={() => setSelectedFundCall(null)}
          onUpdate={() => {
            loadFundCalls();
            setSelectedFundCall(null);
          }}
        />
      )}
    </div>
  );
}
